import HubIcon from '@mui/icons-material/Hub';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Paper, Skeleton, Tooltip, Typography, type SvgIconProps } from '@mui/material';
import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import {
  useDepletionsKpis,
  useDepletionsTrend,
  useFollowUpTracker,
  useNewVsLostAccounts,
  useTopAccounts,
} from '../api/depletions';
import { fmt6PkNumber } from '../lib/volume';
import { colors, typographyTokens } from '../theme';
import { SectionExplainer } from './SectionExplainer';

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const wholeFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// Compact "Y 6-pk · X 9L" — used in tooltip lines and tile subs where
// we want both units on the same line without crowding. 6-pack leads
// as the primary unit; the 9L equivalent trails.
function fmt9LAnd6Pk(cases9L: number): string {
  return `${fmt6PkNumber(cases9L)} 6-pk · ${numberFormatter.format(cases9L)} 9L`;
}

// Signed delta variant: "+Y 6-pk · +X 9L" or "-Y 6-pk · -X 9L".
function fmtDiff9LAnd6Pk(cases9L: number): string {
  const sign = cases9L >= 0 ? '+' : '';
  return `${sign}${fmt6PkNumber(cases9L)} 6-pk · ${sign}${numberFormatter.format(cases9L)} 9L`;
}

// Ease-out cubic — matches the MVP feel without bouncing.
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function useCountUp(target: number, duration = 1200, delay = 0): number {
  const [value, setValue] = useState(0);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (target === 0) {
      setValue(0);
      return;
    }

    let cancelled = false;
    const t = window.setTimeout(() => {
      const tick = (now: number) => {
        if (cancelled) return;
        if (startRef.current === null) startRef.current = now;
        const progress = Math.min((now - startRef.current) / duration, 1);
        const eased = easeOutCubic(progress);
        setValue(eased * target);
        if (progress < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setValue(target);
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startRef.current = null;
    };
  }, [target, duration, delay]);

  return value;
}

interface KpiItem {
  label: string;
  rawValue: number;
  format: (v: number) => string;
  sub: string;
  icon: ComponentType<SvgIconProps>;
  color: string;
  tooltip: string;
}

interface AnimatedValueProps {
  value: number;
  format: (v: number) => string;
  color: string;
  delay: number;
}

function AnimatedValue({ value, format, color, delay }: AnimatedValueProps) {
  const animated = useCountUp(value, 1200, delay);
  return (
    <Typography
      sx={{
        ...typographyTokens.heroStat,
        // Matches MVP: slim 17/19 px values that read as refined rather
        // than bulky. Heavier or larger numbers crowd the tile and
        // make the strip feel chunky.
        fontSize: { xs: 17, md: 19 },
        color,
      }}
    >
      {format(animated)}
    </Typography>
  );
}

const tooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: '#fff',
      borderRadius: 1.5,
      border: `1px solid ${colors.border}`,
      px: 2,
      py: 1.5,
      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
      maxWidth: 280,
      color: colors.textPrimary,
    },
  },
  arrow: {
    sx: {
      color: '#fff',
      '&::before': { border: `1px solid ${colors.border}` },
    },
  },
};

export function DepletionsKpiStrip() {
  const kpisQ = useDepletionsKpis();
  const trendQ = useDepletionsTrend();
  const followQ = useFollowUpTracker();
  // 3-month window = ~90 days, matches the "Active (90d)" framing.
  const newLostQ = useNewVsLostAccounts(3);
  // Top 10 by lifetime 9L — powers the concentration KPI.
  const topQ = useTopAccounts(undefined, 10);

  const isLoading =
    kpisQ.isLoading ||
    trendQ.isLoading ||
    followQ.isLoading ||
    newLostQ.isLoading ||
    topQ.isLoading;
  const error = kpisQ.error ?? trendQ.error ?? followQ.error ?? newLostQ.error ?? topQ.error;

  const derived = useMemo(() => {
    if (!kpisQ.data || !trendQ.data || !followQ.data || !newLostQ.data || !topQ.data) return null;

    const totalVolume = Number(kpisQ.data.total_9l);
    const uniqueAccounts = kpisQ.data.unique_accounts;
    const uniqueStates = kpisQ.data.unique_states;
    const uniqueProducts = kpisQ.data.unique_products;

    // YoY: sum of the most recent 12 months vs the 12 months before.
    const points = trendQ.data.points;
    const last12 = points.slice(-12);
    const prior12 = points.slice(-24, -12);
    const last12Vol = last12.reduce((s, p) => s + Number(p.cases_9l), 0);
    const prior12Vol = prior12.reduce((s, p) => s + Number(p.cases_9l), 0);
    const yoyGrowthPct = prior12Vol > 0 ? ((last12Vol - prior12Vol) / prior12Vol) * 100 : 0;
    const yoyDiff = last12Vol - prior12Vol;

    // MoM: most recent month vs the one before. Captures inflection
    // points (season changes, rep moves) that YoY's broad window misses.
    const lastMonth = points[points.length - 1];
    const priorMonth = points[points.length - 2];
    const lastMonthVol = lastMonth ? Number(lastMonth.cases_9l) : 0;
    const priorMonthVol = priorMonth ? Number(priorMonth.cases_9l) : 0;
    const momGrowthPct =
      priorMonthVol > 0 ? ((lastMonthVol - priorMonthVol) / priorMonthVol) * 100 : 0;
    const momDiff = lastMonthVol - priorMonthVol;
    const lastMonthLabel = lastMonth?.period ?? null;

    // Recent quarter: last 3 months of trend.
    const recentQ = points.slice(-3).reduce((s, p) => s + Number(p.cases_9l), 0);

    // Active in last 90d vs at risk — both derived from follow-up tracker.
    const buckets = followQ.data.buckets;
    const activeAccounts = buckets
      .filter((b) => ['0-30 days', '30-60 days', '60-90 days'].includes(b.label))
      .reduce((s, b) => s + b.count, 0);
    const atRiskAccounts = buckets.find((b) => b.label === '120+ days')?.count ?? 0;
    const accountBase = buckets.reduce((s, b) => s + b.count, 0);
    const activePct = accountBase > 0 ? (activeAccounts / accountBase) * 100 : 0;
    const atRiskPct = accountBase > 0 ? (atRiskAccounts / accountBase) * 100 : 0;
    const recentPct = totalVolume > 0 ? (recentQ / totalVolume) * 100 : 0;

    // Net account change (90d) = accounts gained − accounts lost.
    // The single best "is the base growing or shrinking right now?" metric.
    const newCount = newLostQ.data.new_count;
    const lostCount = newLostQ.data.lost_count;
    const netAccountChange = newCount - lostCount;

    // Top-10 concentration — share of total lifetime 9L from the
    // top 10 accounts. Climbing concentration = rising relationship risk.
    const top10Volume = topQ.data.accounts.slice(0, 10).reduce((s, a) => s + Number(a.cases_9l), 0);
    const top10Pct = totalVolume > 0 ? (top10Volume / totalVolume) * 100 : 0;

    return {
      totalVolume,
      uniqueAccounts,
      uniqueStates,
      uniqueProducts,
      yoyGrowthPct,
      yoyDiff,
      last12Vol,
      prior12Vol,
      momGrowthPct,
      momDiff,
      lastMonthVol,
      priorMonthVol,
      lastMonthLabel,
      recentQ,
      activeAccounts,
      atRiskAccounts,
      activePct,
      atRiskPct,
      recentPct,
      newCount,
      lostCount,
      netAccountChange,
      top10Volume,
      top10Pct,
    };
  }, [kpisQ.data, trendQ.data, followQ.data, newLostQ.data, topQ.data]);

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: `1px solid ${colors.error}40`,
          bgcolor: `${colors.error}05`,
        }}
      >
        <Typography color="error">Failed to load depletions KPIs: {error.message}</Typography>
      </Paper>
    );
  }

  if (isLoading || !derived) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 1.5,
        }}
      >
        {Array.from({ length: 9 }).map((_, i) => (
          <Paper
            key={i}
            elevation={0}
            sx={{ p: 2, border: `1px solid ${colors.border}`, borderRadius: 2 }}
          >
            <Skeleton width="60%" />
            <Skeleton width="80%" height={28} sx={{ mt: 0.5 }} />
            <Skeleton width="50%" />
          </Paper>
        ))}
      </Box>
    );
  }

  const yoyPositive = derived.yoyGrowthPct >= 0;
  const yoyColor = yoyPositive ? colors.success : colors.error;
  const momPositive = derived.momGrowthPct >= 0;
  const momColor = momPositive ? colors.success : colors.error;
  const netPositive = derived.netAccountChange >= 0;
  const netColor = netPositive ? colors.success : colors.error;
  // Concentration risk: ≥40% from top 10 starts to feel risky for a
  // base of thousands. Color the tile by tier.
  const concentrationColor =
    derived.top10Pct >= 50 ? colors.error : derived.top10Pct >= 35 ? colors.warning : colors.info;

  // Story-ordered: SCALE → TRAJECTORY → HEALTH.
  //   Scale     — Total Volume · Accounts
  //   Trajectory — YoY · MoM · Recent Q
  //   Health    — Active (90d) · Net Change · At Risk · Top-10
  const items: KpiItem[] = [
    {
      label: 'Total Volume',
      rawValue: derived.totalVolume,
      format: (v) => fmt6PkNumber(v),
      sub: `6-pk · ${numberFormatter.format(derived.totalVolume)} 9L`,
      icon: LocalShippingIcon,
      color: colors.chartPrimary,
      tooltip: `Total 6-pack equivalent volume depleted across all accounts (1 9L case = 2 six-packs).\n\nLast 12 months: ${fmt9LAnd6Pk(derived.last12Vol)}\nPrior 12 months: ${fmt9LAnd6Pk(derived.prior12Vol)}\n\nReported across ${derived.uniqueStates} states · ${derived.uniqueProducts} active SKUs.`,
    },
    {
      label: 'Accounts',
      rawValue: derived.uniqueAccounts,
      format: (v) => wholeFormatter.format(v),
      sub: `${derived.uniqueStates} states · ${derived.uniqueProducts} SKUs`,
      icon: StorefrontIcon,
      color: colors.info,
      tooltip: `Unique retail accounts iDIG reports depletion data for.\n\n${wholeFormatter.format(derived.uniqueAccounts)} accounts across ${derived.uniqueStates} states, carrying ${derived.uniqueProducts} active product SKUs.\n\nState count and SKU count moved into this tile to free strip space for higher-information KPIs (momentum, base-health).`,
    },
    {
      label: 'YoY Growth',
      rawValue: Math.abs(derived.yoyGrowthPct),
      format: (v) => `${yoyPositive ? '+' : '-'}${v.toFixed(1)}%`,
      sub: fmtDiff9LAnd6Pk(derived.yoyDiff),
      icon: TrendingUpIcon,
      color: yoyColor,
      tooltip: `Year-over-year change: most recent 12 months vs the 12 months before that.\n\nLast 12 months: ${fmt9LAnd6Pk(derived.last12Vol)}\nPrior 12 months: ${fmt9LAnd6Pk(derived.prior12Vol)}\nDifference: ${fmtDiff9LAnd6Pk(derived.yoyDiff)}\n\nYoY is the brand-narrative growth number; pair it with MoM to spot fresh inflection points.`,
    },
    {
      label: 'MoM Momentum',
      rawValue: Math.abs(derived.momGrowthPct),
      format: (v) => `${momPositive ? '+' : '-'}${v.toFixed(1)}%`,
      sub: `${fmtDiff9LAnd6Pk(derived.momDiff)} vs prior mo`,
      icon: TimelineIcon,
      color: momColor,
      tooltip: `Month-over-month change in 6-pk volume — most recent month vs the one before.\n\nLast month: ${fmt9LAnd6Pk(derived.lastMonthVol)}\nPrior month: ${fmt9LAnd6Pk(derived.priorMonthVol)}\nDifference: ${fmtDiff9LAnd6Pk(derived.momDiff)}\n\nMoM catches inflection points (season changes, rep moves, a new account ramping) that YoY's broad window smooths over. Watch when MoM and YoY disagree.`,
    },
    {
      label: 'Recent Q',
      rawValue: derived.recentQ,
      format: (v) => fmt6PkNumber(v),
      sub: `${numberFormatter.format(derived.recentQ)} 9L · ${derived.recentPct.toFixed(0)}% of all-time`,
      icon: ShowChartIcon,
      color: '#06b6d4',
      tooltip: `Volume depleted in the most recent quarter (last 3 months of available data).\n\n${fmt9LAnd6Pk(derived.recentQ)} equivalents, ${derived.recentPct.toFixed(0)}% of all-time volume — current-momentum indicator that balances YoY (slow) and MoM (noisy).`,
    },
    {
      label: 'Active (90d)',
      rawValue: derived.activeAccounts,
      format: (v) => wholeFormatter.format(v),
      sub: `${derived.activePct.toFixed(0)}% of base`,
      icon: InventoryIcon,
      color: colors.success,
      tooltip: `Accounts that moved product within the last 90 days — these are healthy, engaged buyers.\n\n${wholeFormatter.format(derived.activeAccounts)} of ${wholeFormatter.format(derived.uniqueAccounts)} accounts (${derived.activePct.toFixed(0)}%). The remaining accounts may need follow-up — see the Follow-Up Tracker.`,
    },
    {
      label: 'Net Change (90d)',
      rawValue: Math.abs(derived.netAccountChange),
      format: (v) => `${netPositive ? '+' : '-'}${wholeFormatter.format(v)}`,
      sub: `+${wholeFormatter.format(derived.newCount)} new · -${wholeFormatter.format(derived.lostCount)} lost`,
      icon: PersonAddIcon,
      color: netColor,
      tooltip: `Accounts gained minus accounts lost in the most recent 90-day window.\n\nNew (active recent, silent prior): ${wholeFormatter.format(derived.newCount)}\nLost (active prior, silent recent): ${wholeFormatter.format(derived.lostCount)}\nNet: ${netPositive ? '+' : ''}${wholeFormatter.format(derived.netAccountChange)}\n\nThis is the single best "is the base growing or shrinking right now?" indicator. Net-positive is the daily-health signal sales managers should track.`,
    },
    {
      label: 'At Risk',
      rawValue: derived.atRiskAccounts,
      format: (v) => wholeFormatter.format(v),
      sub: `${derived.atRiskPct.toFixed(0)}% · 120+ days`,
      icon: WarningAmberIcon,
      color: colors.error,
      tooltip: `Accounts with no movement in 120+ days — high churn risk.\n\n${wholeFormatter.format(derived.atRiskAccounts)} accounts (${derived.atRiskPct.toFixed(0)}% of total base) have gone silent. Prioritize these for outreach — they're the rescue list.`,
    },
    {
      label: 'Top-10 Share',
      rawValue: derived.top10Pct,
      format: (v) => `${v.toFixed(0)}%`,
      sub: `${fmt6PkNumber(derived.top10Volume)} of ${fmt6PkNumber(derived.totalVolume)} 6-pk (${numberFormatter.format(derived.top10Volume)} / ${numberFormatter.format(derived.totalVolume)} 9L)`,
      icon: HubIcon,
      color: concentrationColor,
      tooltip: `Share of total 6-pk volume contributed by the top 10 lifetime accounts.\n\nTop 10 = ${fmt9LAnd6Pk(derived.top10Volume)}\nTotal  = ${fmt9LAnd6Pk(derived.totalVolume)}\nShare  = ${derived.top10Pct.toFixed(1)}%\n\nConcentration risk indicator. Climbing share = rising dependency on a handful of relationships. Watch for sharp jumps — they usually mean a tail account stopped ordering, not that the top accounts grew.`,
    },
  ];

  return (
    <>
      {/* Desktop: unified slim strip */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          bgcolor: '#fff',
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.05)' },
        }}
      >
        {items.map((item, i) => (
          <Tooltip
            key={item.label}
            arrow
            placement="bottom"
            slotProps={tooltipSlotProps}
            title={
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: item.color, mb: 0.75 }}>
                  {item.label}
                </Typography>
                {item.tooltip.split('\n\n').map((para, j) => (
                  <Typography
                    key={j}
                    sx={{
                      fontSize: 12,
                      color: colors.textSecondary,
                      lineHeight: 1.55,
                      mb: j < item.tooltip.split('\n\n').length - 1 ? 1 : 0,
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {para}
                  </Typography>
                ))}
              </Box>
            }
          >
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                cursor: 'help',
                borderRight: i < items.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: colors.surfaceSubtle },
              }}
            >
              {/* Color accent bar — slim, MVP-style */}
              <Box
                sx={{
                  width: 3,
                  height: 28,
                  borderRadius: 2,
                  bgcolor: item.color,
                  opacity: 0.5,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
                  <item.icon sx={{ fontSize: 12, color: colors.textMuted }} />
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <AnimatedValue
                  value={item.rawValue}
                  format={item.format}
                  color={item.color}
                  delay={i * 70}
                />
                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: colors.textMuted,
                    mt: 0.2,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  {item.sub}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Tablet / Mobile: compact grid */}
      <Box
        sx={{
          display: { xs: 'grid', lg: 'none' },
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
          bgcolor: '#fff',
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}
      >
        {items.map((item, i) => (
          <Tooltip
            key={item.label}
            arrow
            placement="bottom"
            slotProps={tooltipSlotProps}
            title={
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: item.color, mb: 0.75 }}>
                  {item.label}
                </Typography>
                <Typography
                  sx={{ fontSize: 12, color: colors.textSecondary, whiteSpace: 'pre-line' }}
                >
                  {item.tooltip}
                </Typography>
              </Box>
            }
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1.5,
                cursor: 'help',
                borderRight: `1px solid ${colors.borderLight}`,
                borderBottom: `1px solid ${colors.borderLight}`,
              }}
            >
              <Box
                sx={{
                  width: 3,
                  height: 28,
                  borderRadius: 2,
                  bgcolor: item.color,
                  opacity: 0.5,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                  <item.icon sx={{ fontSize: 11, color: colors.textMuted }} />
                  <Typography
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <AnimatedValue
                  value={item.rawValue}
                  format={item.format}
                  color={item.color}
                  delay={i * 50}
                />
                <Typography sx={{ fontSize: 10, color: colors.textMuted, mt: 0.15 }}>
                  {item.sub}
                </Typography>
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Reader's guide — collapsed by default, right-aligned so the
          pill anchors to the KPI strip's right edge instead of floating. */}
      <SectionExplainer
        align="right"
        rows={[
          {
            sample: <KpiSampleTile color={colors.chartPrimary} value="65K" />,
            term: 'KPI tile',
            desc: (
              <>
                Big number is the headline metric; the muted line below names the unit (
                <strong>6-pk · N 9L</strong> for volume) or a contextual sub-stat. Hover any tile
                for the full definition.
              </>
            ),
          },
          {
            sample: <KpiSampleTile color={colors.success} value="+12%" />,
            term: 'Growth color (green / red)',
            desc: (
              <>
                <strong>Green</strong> = up vs prior period; <strong>red</strong> = down. The
                threshold is meaningful change — within ±1% reads as flat in muted grey.
              </>
            ),
          },
          {
            sample: <KpiSampleTile color="#06b6d4" value="3M" />,
            term: 'Time windows',
            desc: (
              <>
                <strong>YoY</strong> = last 12 months vs the 12 before. <strong>MoM</strong> = last
                month vs the one before. <strong>Recent Q</strong> = the last 3 months. Pair the
                slow signal (YoY) with the fast one (MoM) to spot inflection.
              </>
            ),
          },
          {
            sample: <KpiSampleTile color={colors.error} value="120+" />,
            term: 'Base-health tiles',
            desc: (
              <>
                <strong>Active (90d)</strong> = ordering recently. <strong>Net change</strong> =
                gained − lost. <strong>At Risk</strong> = silent ≥120 days. The Follow-Up Tracker
                section turns these into call lists.
              </>
            ),
          },
          {
            sample: <KpiSampleTile color="#8b5cf6" value="61%" />,
            term: 'Top-10 share (concentration)',
            desc: (
              <>
                What fraction of total 6-pk comes from your top-10 accounts. Climbing share = rising
                dependency on a handful of relationships — watch for sharp jumps.
              </>
            ),
          },
        ]}
      />
    </>
  );
}

// Mini-tile sample used inside the KPI strip's SectionExplainer.
function KpiSampleTile({ color, value }: { color: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0,
        px: 0.6,
        py: 0.35,
        borderRadius: 0.75,
        bgcolor: `${color}10`,
        border: `1px solid ${color}33`,
        lineHeight: 1.1,
      }}
    >
      <Box
        component="span"
        sx={{ fontSize: 10, fontWeight: 800, color, fontFeatureSettings: '"tnum"' }}
      >
        {value}
      </Box>
    </Box>
  );
}
